import React from 'react';

export type PeriodType = '7days' | '30days' | '1year' | 'custom';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface PeriodSelectorProps {
  selectedPeriod: PeriodType;
  onPeriodChange: (period: PeriodType, dateRange: DateRange) => void;
  // Dates actuelles passees par le parent pour un controle total
  currentRange: DateRange;
}

// Helper to get date range based on period type
export const getDateRangeForPeriod = (period: PeriodType, customStart?: Date, customEnd?: Date): DateRange => {
  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  let startDate: Date;

  switch (period) {
    case '7days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '30days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '1year':
      startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() - 1);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      startDate = customStart || new Date(now.setDate(now.getDate() - 30));
      startDate.setHours(0, 0, 0, 0);
      if (customEnd) {
        endDate.setTime(customEnd.getTime());
        endDate.setHours(23, 59, 59, 999);
      }
      break;
    default:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
  }

  return { startDate, endDate };
};

// Format date to DD/MM/YYYY for display
export const formatDateForDisplay = (date: Date): string => {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

// Format date to YYYY-MM-DD for input[type="date"]
const formatDateForInput = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  currentRange
}) => {

  const handlePeriodSelectChange = (period: PeriodType) => {
    if (period === 'custom') {
      // Garder les dates actuelles lors du passage en custom
      onPeriodChange('custom', currentRange);
    } else {
      // Calculer les nouvelles dates pour les periodes predefinies
      const newRange = getDateRangeForPeriod(period);
      onPeriodChange(period, newRange);
    }
  };

  const handleDateInputChange = (type: 'start' | 'end', value: string) => {
    const newDate = new Date(value);
    if (isNaN(newDate.getTime())) return;

    const newRange = { ...currentRange };
    if (type === 'start') {
      newDate.setHours(0, 0, 0, 0);
      newRange.startDate = newDate;
    } else {
      newDate.setHours(23, 59, 59, 999);
      newRange.endDate = newDate;
    }

    onPeriodChange('custom', newRange);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Periode :
      </span>

      <select
        value={selectedPeriod}
        onChange={(e) => handlePeriodSelectChange(e.target.value as PeriodType)}
        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
      >
        <option value="7days">7 derniers jours</option>
        <option value="30days">30 derniers jours</option>
        <option value="1year">1 an</option>
        <option value="custom">Periode personnalisee</option>
      </select>

      {selectedPeriod === 'custom' && (
        <div className="flex items-center gap-4 ml-2">
          <div className="flex items-center gap-2">
            <label htmlFor="startDate" className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
              Du :
            </label>
            <input
              id="startDate"
              type="date"
              value={formatDateForInput(currentRange.startDate)}
              max={formatDateForInput(currentRange.endDate)}
              onChange={(e) => handleDateInputChange('start', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="endDate" className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
              Au :
            </label>
            <input
              id="endDate"
              type="date"
              value={formatDateForInput(currentRange.endDate)}
              min={formatDateForInput(currentRange.startDate)}
              max={formatDateForInput(new Date())}
              onChange={(e) => handleDateInputChange('end', e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>
        </div>
      )}
    </div>
  );
};
